import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer } from "lucide-react";
import { SITE, HOUSE_RULES } from "@/data/site";
import useProperties from "@/hooks/useProperties";
import Seo from "@/components/Seo";
import { getPhotos, fileUrl } from "@/lib/api";

const Sheet = ({ property, compact = false }) => {
  const url = `${window.location.origin}/stay/${property.slug}`;
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    getPhotos(property.slug)
      .then((res) => setUploaded(res.data.map((ph) => fileUrl(ph.storage_path))))
      .catch(() => setUploaded([]));
  }, [property.slug]);

  const photos = [...new Set([property.hero, ...(property.gallery || []), ...uploaded])].slice(
    0,
    compact ? 2 : 4
  );

  return (
    <section className="handout-sheet" data-testid={`handout-${property.slug}`}>
      <div className="flex items-start justify-between gap-8 border-b-2 border-[#B08A3E] pb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#B08A3E]">{property.location}</p>
          <h2 className="font-display text-3xl text-[#0F2A52] mt-1">{property.name}</h2>
          {property.tagline && <p className="text-sm text-[#4A4A4A] mt-1">{property.tagline}</p>}
        </div>
        <div className="text-center shrink-0">
          <QRCodeSVG value={url} size={78} fgColor="#0F2A52" bgColor="#FFFFFF" level="M" />
          <p className="text-[9px] text-[#4A4A4A] mt-1 leading-tight">
            Scan for photos
            <br />&amp; full details
          </p>
        </div>
      </div>

      <div className={`grid gap-2 mt-4 ${compact ? "grid-cols-2" : "grid-cols-4"}`}>
        {photos.map((src, i) => (
          <img
            key={src + i}
            src={src}
            alt={i === 0 ? property.heroAlt || property.name : `${property.name}, ${property.location}`}
            className="w-full h-24 object-cover"
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-1 mt-4 text-sm text-[#1A1A1A]">
        <span>
          <strong>{property.bedrooms}</strong> {property.bedrooms === 1 ? "bedroom" : "bedrooms"}
        </span>
        <span>
          <strong>{property.bathrooms}</strong> {property.bathrooms === 1 ? "bathroom" : "bathrooms"}
        </span>
        <span>
          Sleeps <strong>{property.sleeps}</strong>
        </span>
        <span>Rates on enquiry</span>
      </div>

      <p className="text-sm text-[#4A4A4A] leading-snug mt-3">{property.short}</p>

      <div className={`mt-4 grid gap-6 ${compact ? "grid-cols-1" : "grid-cols-2"}`}>
        <div>
          <h3 className="text-[10px] uppercase tracking-[0.24em] text-[#B08A3E]">Highlights</h3>
          <ul className="mt-2 space-y-0.5">
            {(property.features || []).slice(0, compact ? 5 : 10).map((f) => (
              <li key={f} className="text-xs text-[#4A4A4A]">
                · {f}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-[10px] uppercase tracking-[0.24em] text-[#B08A3E]">Included</h3>
          <p className="text-xs text-[#4A4A4A] mt-2 leading-relaxed">
            {(property.amenities || []).slice(0, compact ? 8 : 16).join(" · ")}
          </p>
          <>
            <h3 className="text-[10px] uppercase tracking-[0.24em] text-[#B08A3E] mt-4">Good to know</h3>
            <p className="text-xs text-[#4A4A4A] mt-2 leading-relaxed">
              {property.minimumStay || HOUSE_RULES.minimumStay} · {HOUSE_RULES.checkIn} ·{" "}
              {HOUSE_RULES.checkOut} · {HOUSE_RULES.rules.join(" · ")}
            </p>
          </>
        </div>
      </div>

      {!compact && <p className="text-xs text-[#4A4A4A] leading-snug mt-4">{property.locationNote}</p>}

      <div className="flex flex-wrap items-baseline justify-between gap-4 mt-5 pt-3 border-t border-[#0F2A52]/20">
        <p className="font-display text-lg text-[#0F2A52]">
          Book direct with {SITE.owner} — save the booking fees
        </p>
        <p className="text-sm text-[#0F2A52]">
          {SITE.phoneDisplay} · {SITE.email} · {SITE.domain}
        </p>
      </div>
    </section>
  );
};

export default function Handout() {
  const { slug } = useParams();
  const properties = useProperties();
  const one = slug ? properties.find((p) => p.slug === slug) : null;
  const sheets = one ? [one] : properties;

  return (
    <main className="handout" data-testid="handout-page">
      <Seo
        title={`PARADISIO RESORTS | ${one ? one.name : "All four homes"} — printable sheet`}
        description="Printable guest summary."
        noindex
      />

      <div className="handout-toolbar">
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-[#0F2A52] text-white px-5 py-3 text-sm rounded-md"
          data-testid="handout-print-btn"
        >
          <Printer size={16} /> Print or save as PDF
        </button>
        <p className="text-xs text-neutral-500 mt-2">
          {one
            ? "One page. Prints on A4 — leave it in the apartment or hand it to a guest."
            : "All four homes on one page, two per side."}
        </p>
      </div>

      <div className="handout-body">
        {sheets.map((p) => (
          <Sheet key={p.slug} property={p} compact={!one} />
        ))}
        {!one && (
          <p className="text-center text-xs text-[#4A4A4A] mt-6">
            PARADISIO RESORTS · {SITE.phoneDisplay} · {SITE.email} · {SITE.domain}
          </p>
        )}
      </div>
    </main>
  );
}
